<template>
    <div class="patent-landscape border-radius-lg">
        <!-- Background overlay when expanded -->
        <div v-if="isExpanded" class="chart-background" @click="collapseChart"></div>

        <AonSkeleton
            class="w-100 h-100 display-flex align-items-center mb-sm flex-wrap"
            :loading="loading"
            :data="0"
            :height="'100%'"
            light
        >
            <!-- Patent Proximus Container -->
            <div
                v-if="!loading"
                class="patent-proximus-container"
                :class="{ expanded: isExpanded, collapsing: isCollapsing }"
            >
                <PatentProximus
                    :config="{
                        authToken: getAuthToken,
                        baseURL: config.external.clusterSvcUrl,
                        jobID: jobID,
                    }"
                    :allow-interaction="allowInteraction"
                    :additional-colorBy="colorByOptions"
                    @interaction-click="expandChart"
                    @status="onProximusStatus"
                />

                <!-- Close button when expanded -->
                <AonButton
                    v-if="isExpanded"
                    class="close-button"
                    label="Close"
                    type="transparent"
                    :text-with-icon="true"
                    :icon-options="{ iconStyle: 'fas', iconName: 'fa-times' }"
                    @clicked="collapseChart()"
                />
            </div>
        </AonSkeleton>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useAuth } from '@/auth/authPlugin'
import { config } from '@/config'

const auth = useAuth()
const emit = defineEmits(['status'])
const props = defineProps({
    jobID: {
        type: String,
        required: true,
    },
    loading: {
        type: Boolean,
        default: true,
    },
    error: {
        type: String,
        default: null,
    },
})

// Reactive state for expanded mode
const isExpanded = ref(false)
const isCollapsing = ref(false)
const allowInteraction = ref(false)
const loadingError = ref(null)
const colorByOptions = ref([
    { fieldName: 'assignees_grouped', displayName: 'Entity Groups' },
    { fieldName: 'ultimate_aon_entities', displayName: 'Ultimate Entities' },
])

const jobID = computed(() => props.jobID)
const loading = computed(() => props.loading)
const error = computed(() => props.error || loadingError)

onMounted(() => {})

const onProximusStatus = (status) => {
    const failureStatuses = [
        'POLLING_FAILED',
        'JOB_PARTS_FAILED',
        'JOB_PARTS_NOT_FOUND',
        'LANDSCAPE_DATA_FAILED',
        'CLUSTER_DATA_FAILED',
        'DATA_MERGE_FAILED',
        'LOADING_FAILED',
    ]

    const isFailure = failureStatuses.includes(status)

    emit('status', status === isFailure ? 'FAILED' : status)
}

const expandChart = () => {
    allowInteraction.value = true
    isExpanded.value = true
    // Prevent body scroll when overlay is open
    document.body.style.overflow = 'hidden'
}

const collapseChart = () => {
    allowInteraction.value = false
    isCollapsing.value = true

    // Wait for collapse animation to complete before hiding
    setTimeout(() => {
        isExpanded.value = false
        isCollapsing.value = false
        // Restore body scroll
        document.body.style.overflow = ''
    }, 300) // Match animation duration
}

const getAuthToken = async () => {
    const token = await auth.getAccessTokenSilently()
    return `Bearer ${token}`
}
</script>

<style scoped lang="scss">
.patent-landscape {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;

    .expand-landscape {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 10;
        cursor: pointer;

        // width: 18px;
        // height: 18px;
        border-radius: 4px;
        padding: 10px !important;

        transition: all 0.3s ease-in-out;

        &:hover {
            background-color: var(--color-stone-300) !important;
        }

        &.up {
            transform: rotate(180deg);
        }
    }
}

.patent-proximus-container {
    width: 100%;
    height: 100%;
    position: relative;
    border: 1px solid var(--color-stone-300);
    border-radius: var(--border-radius-lg);

    &.expanded {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 10000;
        background: white;

        // Smooth scale and fade animation
        animation: expandChart 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;

        &.collapsing {
            animation: collapseChart 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19) forwards;
        }
    }

    .patent-proximus {
        width: 100%;
        height: 100%;
        transition: opacity 0.2s ease-in-out;
    }

    .close-button {
        position: absolute;
        bottom: 20px;
        right: 20px;
        z-index: 10001;
        opacity: 0;
        animation: fadeInButton 0.3s ease-in-out 0.2s forwards;

        transform: translateX(-50%);
    }
}

@keyframes expandChart {
    0% {
        transform: scale(0.95);
        opacity: 0;
    }
    100% {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes collapseChart {
    0% {
        transform: scale(1);
        opacity: 1;
    }
    100% {
        transform: scale(0.95);
        opacity: 0;
    }
}

@keyframes fadeInButton {
    0% {
        opacity: 0;
        transform: translateY(-10px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
}

.chart-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    animation: fadeInOverlay 0.3s ease-out forwards;
}

@keyframes fadeInOverlay {
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}
</style>
